'use client';

import { c as _c } from "react/compiler-runtime";
import { Menu, Search, ShoppingBag, User, X } from 'lucide-react';
import dynamic from 'next/dynamic';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import type { ReactNode } from 'react';
import { Suspense, startTransition, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useCartSummary } from '@/components/cart/cart-provider';
import type { CartSettingsConfig } from '@/lib/cart-settings';
import { hasCustomerAuthHint } from '@/lib/customer-auth-hint';
import { toCanonicalPath } from '@/lib/markets';
import type { AccountSettings } from '@/lib/site-settings';
import { useBrowserLocation } from '@/lib/use-browser-location';
import { useHeaderRuntime } from './header-runtime';
import { getAccountDrawerErrorMessage, type MobileLearnLink, type MobileNeedHelpLink, type NavItem, type PopularProduct, type TrendingSearch } from './site-header.shared';

/**
 * Runs synchronously while the header HTML is parsed — before first paint and
 * before any JavaScript bundle loads — and copies the `cartQty` cookie (kept in
 * sync by CartProvider) into the badge rendered just before it. Kept
 * dependency-free and wrapped in try/catch; a missing/invalid cookie leaves the
 * server-rendered `0` (hidden) in place.
 */
const CART_COUNT_PREPAINT_SCRIPT = `(function(){try{var m=document.cookie.match(/(?:^|;\\s*)cartQty=(\\d+)/);var n=m?parseInt(m[1],10):0;if(!(n>0))return;var s=document.currentScript;var b=s&&s.previousElementSibling;var el=b&&b.querySelector('[data-cart-count]');if(!el)return;el.textContent=String(n);el.setAttribute('data-cart-count',String(n));}catch(e){}})();`;
const loadMobileMenu = () => import('./mobile-menu');
const MobileMenu = dynamic(() => loadMobileMenu().then(module => module.MobileMenu), {
  ssr: false
});
const loadSearchPopdown = () => import('@/components/search/search-popdown');
const SearchPopdown = dynamic(() => loadSearchPopdown().then(module => module.SearchPopdown), {
  ssr: false
});
const loadCartDrawer = () => import('@/components/cart/cart-drawer');
const CartDrawer = dynamic(() => loadCartDrawer().then(module => module.CartDrawer), {
  ssr: false
});
const loadAccountDrawer = () => import('@/components/account/account-drawer');
const AccountDrawer = dynamic(() => loadAccountDrawer().then(module => module.AccountDrawer), {
  ssr: false
});
interface SiteHeaderControlsProps {
  logo: ReactNode;
  desktopNavigation: ReactNode;
  logoText?: string | null;
  logoUrl?: string | null;
  mobileNeedHelpHeading?: string;
  mobileNeedHelpLinks?: MobileNeedHelpLink[];
  mobileShowAccountLink?: boolean;
  mobileLearnHeading?: string;
  mobileLearnLinks?: MobileLearnLink[];
  navigation?: NavItem[];
  cartSettings: CartSettingsConfig;
  accountSettings?: AccountSettings;
  enableCustomerAccounts?: boolean;
  enableReviews?: boolean;
  popularProducts?: PopularProduct[];
  popularProductHandles?: string[];
  popularProductsHeading?: string;
  trendingSearches?: TrendingSearch[];
}
function getNewlyOpenedPanel({
  accountDrawerOpen,
  cartOpen,
  mobileMenuOpen,
  previousAccountDrawerOpen,
  previousCartOpen,
  previousMobileMenuOpen
}: {
  accountDrawerOpen: boolean;
  cartOpen: boolean;
  mobileMenuOpen: boolean;
  previousAccountDrawerOpen: boolean;
  previousCartOpen: boolean;
  previousMobileMenuOpen: boolean;
}) {
  if (cartOpen && !previousCartOpen) {
    return 'cart';
  }
  if (mobileMenuOpen && !previousMobileMenuOpen) {
    return 'menu';
  }
  if (accountDrawerOpen && !previousAccountDrawerOpen) {
    return 'account';
  }
  return null;
}
function CartQueryOpener(t0) {
  const $ = _c(8);
  const {
    onOpenCart
  } = t0;
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.toString();
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const search = t1;
  let t2;
  let t3;
  if ($[2] !== onOpenCart || $[3] !== pathname || $[4] !== router || $[5] !== search) {
    t2 = () => {
      if (!pathname) {
        return;
      }
      const searchParams_0 = new URLSearchParams(search);
      if (searchParams_0.get("cart") !== "open") {
        return;
      }
      onOpenCart();
      const nextParams = new URLSearchParams(searchParams_0.toString());
      nextParams.delete("cart");
      const nextUrl = nextParams.size > 0 ? `${pathname}?${nextParams.toString()}` : pathname;
      router.replace(nextUrl, {
        scroll: false
      });
    };
    t3 = [onOpenCart, pathname, router, search];
    $[2] = onOpenCart;
    $[3] = pathname;
    $[4] = router;
    $[5] = search;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  return null;
}
export function SiteHeaderControls(t0) {
  const $ = _c(117);
  const {
    logo,
    desktopNavigation,
    logoText,
    logoUrl,
    mobileNeedHelpHeading,
    mobileNeedHelpLinks,
    mobileShowAccountLink,
    mobileLearnHeading,
    mobileLearnLinks,
    navigation: t1,
    cartSettings: staticCartSettings,
    accountSettings,
    enableCustomerAccounts: t2,
    enableReviews: t3,
    popularProducts: t4,
    popularProductHandles: t5,
    popularProductsHeading,
    trendingSearches
  } = t0;
  let t6;
  if ($[0] !== t1) {
    t6 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const navigation = t6;
  const staticEnableCustomerAccounts = t2 === undefined ? false : t2;
  const staticEnableReviews = t3 === undefined ? false : t3;
  let t7;
  if ($[2] !== t4) {
    t7 = t4 === undefined ? [] : t4;
    $[2] = t4;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const popularProducts = t7;
  let t8;
  if ($[4] !== t5) {
    t8 = t5 === undefined ? [] : t5;
    $[4] = t5;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const popularProductHandles = t8;
  const headerRuntime = useHeaderRuntime();
  const cartSettings = headerRuntime?.cartSettings ?? staticCartSettings;
  const enableCustomerAccounts = headerRuntime?.enableCustomerAccounts ?? staticEnableCustomerAccounts;
  const enableReviews = headerRuntime?.enableReviews ?? staticEnableReviews;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountDrawerOpen, setAccountDrawerOpen] = useState(false);
  const [accountDrawerError, setAccountDrawerError] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [hasMountedMobileMenu, setHasMountedMobileMenu] = useState(false);
  const [hasMountedSearch, setHasMountedSearch] = useState(false);
  const [hasMountedCartDrawer, setHasMountedCartDrawer] = useState(false);
  const [hasMountedAccountDrawer, setHasMountedAccountDrawer] = useState(false);
  const {
    quantity: cartQuantity,
    isOpen: cartOpen,
    openCart,
    closeCart
  } = useCartSummary();
  const {
    pathname
  } = useBrowserLocation();
  const router = useRouter();
  const shellRef = useRef(null);
  const previousCartOpenRef = useRef(cartOpen);
  const previousMobileMenuOpenRef = useRef(mobileMenuOpen);
  const previousAccountDrawerOpenRef = useRef(accountDrawerOpen);
  const [headerOffset, setHeaderOffset] = useState(44);
  let t9;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = t10 => {
      const errorMessage = t10 === undefined ? null : t10;
      setHasMountedAccountDrawer(true);
      setAccountDrawerError(errorMessage);
      setAccountDrawerOpen(true);
    };
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const openAccountDrawer = t9;
  let t10;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setAccountDrawerOpen(false);
      setAccountDrawerError(null);
    };
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  const closeAccountDrawer = t10;
  let t11;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      loadAccountDrawer().then(() => {
        startTransition(() => setHasMountedAccountDrawer(true));
      });
    };
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  const warmAccountDrawer = t11;
  let t12;
  if ($[9] !== cartQuantity || $[10] !== cartSettings.emptyCartCollections) {
    t12 = () => {
      loadCartDrawer().then(module => {
        if (cartQuantity === 0) {
          module.warmEmptyCartCollections(cartSettings.emptyCartCollections);
        }
      });
    };
    $[9] = cartQuantity;
    $[10] = cartSettings.emptyCartCollections;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  const warmCartDrawer = t12;
  let t13;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setHasMountedMobileMenu(true);
      setMobileMenuOpen(_temp);
    };
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  const toggleMobileMenu = t13;
  let t14;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setHasMountedSearch(true);
      setSearchOpen(true);
    };
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  const openSearch = t14;
  let t15;
  if ($[14] !== openCart) {
    t15 = () => {
      setHasMountedCartDrawer(true);
      openCart();
    };
    $[14] = openCart;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  const handleOpenCart = t15;
  let t16;
  let t17;
  if ($[16] !== accountDrawerOpen || $[17] !== cartOpen || $[18] !== closeCart || $[19] !== mobileMenuOpen) {
    t16 = function syncHeaderOverlays() {
      bb65: switch (getNewlyOpenedPanel({
        accountDrawerOpen,
        cartOpen,
        mobileMenuOpen,
        previousAccountDrawerOpen: previousAccountDrawerOpenRef.current,
        previousCartOpen: previousCartOpenRef.current,
        previousMobileMenuOpen: previousMobileMenuOpenRef.current
      })) {
        case "cart":
          {
            if (mobileMenuOpen) {
              setMobileMenuOpen(false);
            }
            if (accountDrawerOpen) {
              closeAccountDrawer();
            }
            break bb65;
          }
        case "menu":
          {
            if (cartOpen) {
              closeCart();
            }
            if (accountDrawerOpen) {
              closeAccountDrawer();
            }
            break bb65;
          }
        case "account":
          {
            if (mobileMenuOpen) {
              setMobileMenuOpen(false);
            }
            if (cartOpen) {
              closeCart();
            }
          }
      }
      previousCartOpenRef.current = cartOpen;
      previousMobileMenuOpenRef.current = mobileMenuOpen;
      previousAccountDrawerOpenRef.current = accountDrawerOpen;
    };
    t17 = [cartOpen, mobileMenuOpen, accountDrawerOpen, closeAccountDrawer, closeCart];
    $[16] = accountDrawerOpen;
    $[17] = cartOpen;
    $[18] = closeCart;
    $[19] = mobileMenuOpen;
    $[20] = t16;
    $[21] = t17;
  } else {
    t16 = $[20];
    t17 = $[21];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[22] !== pathname) {
    t18 = () => {
      if (!pathname) {
        return;
      }
      setMobileMenuOpen(false);
      setSearchOpen(false);
    };
    t19 = [pathname];
    $[22] = pathname;
    $[23] = t18;
    $[24] = t19;
  } else {
    t18 = $[23];
    t19 = $[24];
  }
  useEffect(t18, t19);
  let t20;
  let t21;
  if ($[25] !== enableCustomerAccounts || $[26] !== pathname || $[27] !== router) {
    t20 = () => {
      if (!enableCustomerAccounts || typeof window === "undefined" || !pathname) {
        return;
      }
      const searchParams = new URLSearchParams(window.location.search);
      const callbackErrorMessage = getAccountDrawerErrorMessage(searchParams.get("error"));
      const shouldOpenFromTrackedShopifyLanding = pathname === "/" && searchParams.has("syclid");
      const shouldOpenAccountDrawer = searchParams.get("account") === "open" || callbackErrorMessage !== null || shouldOpenFromTrackedShopifyLanding;
      if (!shouldOpenAccountDrawer) {
        return;
      }
      openAccountDrawer(callbackErrorMessage);
      const nextParams = new URLSearchParams(searchParams.toString());
      nextParams.delete("account");
      nextParams.delete("error");
      if (shouldOpenFromTrackedShopifyLanding) {
        nextParams.delete("syclid");
      }
      const nextUrl = nextParams.size > 0 ? `${toCanonicalPath(window.location.pathname)}?${nextParams.toString()}` : toCanonicalPath(window.location.pathname);
      router.replace(nextUrl, {
        scroll: false
      });
    };
    t21 = [enableCustomerAccounts, openAccountDrawer, pathname, router];
    $[25] = enableCustomerAccounts;
    $[26] = pathname;
    $[27] = router;
    $[28] = t20;
    $[29] = t21;
  } else {
    t20 = $[28];
    t21 = $[29];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[30] !== enableCustomerAccounts || $[31] !== mobileMenuOpen) {
    t22 = () => {
      if (!mobileMenuOpen) {
        return;
      }
      setHasMountedMobileMenu(true);
      if (enableCustomerAccounts) {
        warmAccountDrawer();
      }
    };
    t23 = [mobileMenuOpen, enableCustomerAccounts, warmAccountDrawer];
    $[30] = enableCustomerAccounts;
    $[31] = mobileMenuOpen;
    $[32] = t22;
    $[33] = t23;
  } else {
    t22 = $[32];
    t23 = $[33];
  }
  useEffect(t22, t23);
  let t24;
  let t25;
  if ($[34] !== enableCustomerAccounts) {
    t24 = () => {
      let cancelled = false;
      const preload = () => {
        const isMobile = window.matchMedia("(max-width: 1023px)").matches;
        const mount = setMounted => () => {
          if (!cancelled) {
            startTransition(() => setMounted(true));
          }
        };
        if (isMobile) {
          loadMobileMenu().then(mount(setHasMountedMobileMenu));
        }
        loadSearchPopdown().then(mount(setHasMountedSearch));
        loadCartDrawer().then(mount(setHasMountedCartDrawer));
        if (enableCustomerAccounts && hasCustomerAuthHint()) {
          loadAccountDrawer().then(mount(setHasMountedAccountDrawer));
        }
      };
      if (typeof requestIdleCallback !== "function") {
        const timer = setTimeout(preload, 1500);
        return () => {
          cancelled = true;
          clearTimeout(timer);
        };
      }
      const idle = requestIdleCallback(preload, {
        timeout: 10000
      });
      return () => {
        cancelled = true;
        cancelIdleCallback(idle);
      };
    };
    t25 = [enableCustomerAccounts];
    $[34] = enableCustomerAccounts;
    $[35] = t24;
    $[36] = t25;
  } else {
    t24 = $[35];
    t25 = $[36];
  }
  useEffect(t24, t25);
  let t26;
  let t27;
  if ($[37] !== searchOpen) {
    t26 = () => {
      if (searchOpen) {
        setHasMountedSearch(true);
      }
    };
    t27 = [searchOpen];
    $[37] = searchOpen;
    $[38] = t26;
    $[39] = t27;
  } else {
    t26 = $[38];
    t27 = $[39];
  }
  useEffect(t26, t27);
  let t28;
  let t29;
  if ($[40] !== cartOpen) {
    t28 = () => {
      if (cartOpen) {
        setHasMountedCartDrawer(true);
      }
    };
    t29 = [cartOpen];
    $[40] = cartOpen;
    $[41] = t28;
    $[42] = t29;
  } else {
    t28 = $[41];
    t29 = $[42];
  }
  useEffect(t28, t29);
  let t30;
  let t31;
  if ($[43] !== accountDrawerOpen) {
    t30 = () => {
      if (accountDrawerOpen) {
        setHasMountedAccountDrawer(true);
      }
    };
    t31 = [accountDrawerOpen];
    $[43] = accountDrawerOpen;
    $[44] = t30;
    $[45] = t31;
  } else {
    t30 = $[44];
    t31 = $[45];
  }
  useEffect(t30, t31);
  let t32;
  let t33;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = () => {
      const shell = shellRef.current;
      const nav = shell?.closest("nav");
      if (!shell || !nav) {
        return;
      }
      const root = document.documentElement;
      let lastOffset = -1;
      let frame = 0;
      const measure = () => {
        frame = 0;
        const nextOffset = Math.ceil(nav.getBoundingClientRect().height);
        if (nextOffset === lastOffset) {
          return;
        }
        lastOffset = nextOffset;
        setHeaderOffset(nextOffset);
        root.style.setProperty("--site-header-offset", `${nextOffset}px`);
      };
      const scheduleMeasure = () => {
        if (frame) {
          return;
        }
        frame = window.requestAnimationFrame(measure);
      };
      const cleanUp = () => {
        if (frame) {
          window.cancelAnimationFrame(frame);
        }
        root.style.removeProperty("--site-header-offset");
        window.removeEventListener("resize", scheduleMeasure);
      };
      measure();
      window.addEventListener("resize", scheduleMeasure);
      if (typeof ResizeObserver === "undefined") {
        return cleanUp;
      }
      const resizeObserver = new ResizeObserver(scheduleMeasure);
      resizeObserver.observe(nav);
      return () => {
        resizeObserver.disconnect();
        cleanUp();
      };
    };
    t33 = [];
    $[46] = t32;
    $[47] = t33;
  } else {
    t32 = $[46];
    t33 = $[47];
  }
  useLayoutEffect(t32, t33);
  let t34;
  if ($[48] !== openCart) {
    t34 = <Suspense fallback={null}><CartQueryOpener onOpenCart={openCart} /></Suspense>;
    $[48] = openCart;
    $[49] = t34;
  } else {
    t34 = $[49];
  }
  const t35 = mobileMenuOpen ? "Close menu" : "Open menu";
  let t36;
  if ($[50] !== mobileMenuOpen) {
    t36 = mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />;
    $[50] = mobileMenuOpen;
    $[51] = t36;
  } else {
    t36 = $[51];
  }
  let t37;
  if ($[52] !== t35 || $[53] !== t36) {
    t37 = <button type="button" className="-ml-2 p-2 text-neutral-900 lg:hidden" onPointerDown={_temp2} onClick={toggleMobileMenu} aria-label={t35}>{t36}</button>;
    $[52] = t35;
    $[53] = t36;
    $[54] = t37;
  } else {
    t37 = $[54];
  }
  let t38;
  if ($[55] !== logo || $[56] !== t37) {
    t38 = <div className="flex items-center gap-0">{t37}{logo}</div>;
    $[55] = logo;
    $[56] = t37;
    $[57] = t38;
  } else {
    t38 = $[57];
  }
  let t39;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <button type="button" className="p-2 text-neutral-700 transition-colors hover:text-neutral-900" onPointerDown={_temp3} onClick={openSearch} aria-label="Search"><Search className="h-5 w-5" /></button>;
    $[58] = t39;
  } else {
    t39 = $[58];
  }
  let t40;
  if ($[59] !== enableCustomerAccounts) {
    t40 = enableCustomerAccounts && <button type="button" onPointerEnter={warmAccountDrawer} onFocus={warmAccountDrawer} onPointerDown={warmAccountDrawer} onClick={() => openAccountDrawer()} className="p-2 text-neutral-700 transition-colors hover:text-neutral-900" aria-label="Account"><User className="h-5 w-5" /></button>;
    $[59] = enableCustomerAccounts;
    $[60] = t40;
  } else {
    t40 = $[60];
  }
  let t41;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <ShoppingBag className="h-5 w-5" />;
    $[61] = t41;
  } else {
    t41 = $[61];
  }
  let t42;
  if ($[62] !== cartQuantity) {
    t42 = <span data-cart-count={cartQuantity} suppressHydrationWarning={true} className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-neutral-900 text-[10px] font-bold text-white data-[cart-count=0]:hidden">{cartQuantity}</span>;
    $[62] = cartQuantity;
    $[63] = t42;
  } else {
    t42 = $[63];
  }
  let t43;
  if ($[64] !== handleOpenCart || $[65] !== t42 || $[66] !== warmCartDrawer) {
    t43 = <button type="button" className="relative p-2 text-neutral-700 transition-colors hover:text-neutral-900" onPointerEnter={warmCartDrawer} onFocus={warmCartDrawer} onPointerDown={warmCartDrawer} onClick={handleOpenCart} aria-label="Open cart">{t41}{t42}</button>;
    $[64] = handleOpenCart;
    $[65] = t42;
    $[66] = warmCartDrawer;
    $[67] = t43;
  } else {
    t43 = $[67];
  }
  let t44;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <script dangerouslySetInnerHTML={{
      __html: CART_COUNT_PREPAINT_SCRIPT
    }} />;
    $[68] = t44;
  } else {
    t44 = $[68];
  }
  let t45;
  if ($[69] !== t40 || $[70] !== t43) {
    t45 = <div className="flex items-center gap-0.5">{t39}{t40}{t43}{t44}</div>;
    $[69] = t40;
    $[70] = t43;
    $[71] = t45;
  } else {
    t45 = $[71];
  }
  let t46;
  if ($[72] !== desktopNavigation || $[73] !== t38 || $[74] !== t45) {
    t46 = <div ref={shellRef} className="flex h-11 items-center justify-between lg:h-12">{t38}{desktopNavigation}{t45}</div>;
    $[72] = desktopNavigation;
    $[73] = t38;
    $[74] = t45;
    $[75] = t46;
  } else {
    t46 = $[75];
  }
  let t47;
  if ($[76] !== enableCustomerAccounts || $[77] !== hasMountedMobileMenu || $[78] !== headerOffset || $[79] !== mobileLearnHeading || $[80] !== mobileLearnLinks || $[81] !== mobileMenuOpen || $[82] !== mobileNeedHelpHeading || $[83] !== mobileNeedHelpLinks || $[84] !== mobileShowAccountLink || $[85] !== navigation) {
    t47 = hasMountedMobileMenu && <MobileMenu isOpen={mobileMenuOpen} needHelpHeading={mobileNeedHelpHeading} needHelpLinks={mobileNeedHelpLinks} learnHeading={mobileLearnHeading} learnLinks={mobileLearnLinks} enableCustomerAccounts={enableCustomerAccounts} showAccountLink={mobileShowAccountLink} onAccountClick={() => {
      setMobileMenuOpen(false);
      openAccountDrawer();
    }} onClose={() => setMobileMenuOpen(false)} navigation={navigation} headerOffset={headerOffset} />;
    $[76] = enableCustomerAccounts;
    $[77] = hasMountedMobileMenu;
    $[78] = headerOffset;
    $[79] = mobileLearnHeading;
    $[80] = mobileLearnLinks;
    $[81] = mobileMenuOpen;
    $[82] = mobileNeedHelpHeading;
    $[83] = mobileNeedHelpLinks;
    $[84] = mobileShowAccountLink;
    $[85] = navigation;
    $[86] = t47;
  } else {
    t47 = $[86];
  }
  let t48;
  if ($[87] !== enableReviews || $[88] !== hasMountedSearch || $[89] !== popularProductHandles || $[90] !== popularProducts || $[91] !== popularProductsHeading || $[92] !== searchOpen || $[93] !== trendingSearches) {
    t48 = hasMountedSearch && <SearchPopdown isOpen={searchOpen} onClose={() => setSearchOpen(false)} enableReviews={enableReviews} popularProducts={popularProducts} popularProductHandles={popularProductHandles} popularProductsHeading={popularProductsHeading} trendingSearches={trendingSearches} />;
    $[87] = enableReviews;
    $[88] = hasMountedSearch;
    $[89] = popularProductHandles;
    $[90] = popularProducts;
    $[91] = popularProductsHeading;
    $[92] = searchOpen;
    $[93] = trendingSearches;
    $[94] = t48;
  } else {
    t48 = $[94];
  }
  let t49;
  if ($[95] !== cartOpen || $[96] !== cartSettings || $[97] !== closeCart || $[98] !== enableReviews || $[99] !== hasMountedCartDrawer) {
    t49 = hasMountedCartDrawer && <CartDrawer isOpen={cartOpen} onClose={closeCart} cartSettings={cartSettings} enableReviews={enableReviews} />;
    $[95] = cartOpen;
    $[96] = cartSettings;
    $[97] = closeCart;
    $[98] = enableReviews;
    $[99] = hasMountedCartDrawer;
    $[100] = t49;
  } else {
    t49 = $[100];
  }
  let t50;
  if ($[101] !== accountDrawerError || $[102] !== accountDrawerOpen || $[103] !== accountSettings?.returnsUrl || $[104] !== accountSettings?.supportEmail || $[105] !== enableCustomerAccounts || $[106] !== hasMountedAccountDrawer || $[107] !== logoText || $[108] !== logoUrl) {
    t50 = enableCustomerAccounts && hasMountedAccountDrawer && <AccountDrawer isOpen={accountDrawerOpen} onClose={closeAccountDrawer} initialErrorMessage={accountDrawerError} logoUrl={logoUrl} logoText={logoText} supportEmail={accountSettings?.supportEmail} returnsUrl={accountSettings?.returnsUrl} />;
    $[101] = accountDrawerError;
    $[102] = accountDrawerOpen;
    $[103] = accountSettings?.returnsUrl;
    $[104] = accountSettings?.supportEmail;
    $[105] = enableCustomerAccounts;
    $[106] = hasMountedAccountDrawer;
    $[107] = logoText;
    $[108] = logoUrl;
    $[109] = t50;
  } else {
    t50 = $[109];
  }
  let t51;
  if ($[110] !== t34 || $[111] !== t46 || $[112] !== t47 || $[113] !== t48 || $[114] !== t49 || $[115] !== t50) {
    t51 = <>{t34}{t46}{t47}{t48}{t49}{t50}</>;
    $[110] = t34;
    $[111] = t46;
    $[112] = t47;
    $[113] = t48;
    $[114] = t49;
    $[115] = t50;
    $[116] = t51;
  } else {
    t51 = $[116];
  }
  return t51;
}
function _temp3() {
  return void loadSearchPopdown();
}
function _temp2() {
  return void loadMobileMenu();
}
function _temp(current) {
  return !current;
}